import { useCallback, useEffect, useMemo, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { useToast } from "../components/ui/Toast";

// PR #65 — Brand-scoped chain list. Reads storesync.v_tenant_chain_overview via
// /api/brand/:slug/chains (session-cookie auth). Distinct from the canonical
// directory MyChains.tsx, which hits the tenant-overlay /api/tenant/chains and
// returns a different (OverlayChain) shape.

interface OverviewRow {
  tenant_chain_id: string;
  tenant_id: string;
  chain_name: string;
  chain_slug: string | null;
  source_chain_id: string | null;
  canonical_location_count: number;
  included_member_count: number;
  cm_direct_count: number;
  wtb_confirmed_count: number;
  adspend_confirmed_count: number;
  account_only_license_count: number;
}

type Filter = "all" | "cm-direct" | "has-wtb" | "has-adspend" | "excluded";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "cm-direct", label: "CM-direct" },
  { key: "has-wtb", label: "WTB-confirmed" },
  { key: "has-adspend", label: "Ad-spend-confirmed" },
  { key: "excluded", label: "Has exclusions" },
];

function Badge({ n, label, cls }: { n: number; label: string; cls: string }) {
  if (!n) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {n} {label}
    </span>
  );
}

export default function BrandMyChains({ slug }: { slug: string }) {
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<OverviewRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ filter });
      if (q.trim()) params.set("q", q.trim());
      const r = await brandApi.get(`/api/brand/${encodeURIComponent(slug)}/chains?${params.toString()}`);
      setRows(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load chains: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [slug, filter, q, toast]);

  // Debounce filter + search into one reload.
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const totals = useMemo(
    () => ({
      chains: rows.length,
      members: rows.reduce((n, r) => n + (r.included_member_count || 0), 0),
    }),
    [rows]
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">My Chains</h2>
          <p className="text-sm text-gray-600 mt-1">
            {loading
              ? "Loading…"
              : `${totals.chains.toLocaleString()} chain${totals.chains === 1 ? "" : "s"} · ${totals.members.toLocaleString()} included location${totals.members === 1 ? "" : "s"}`}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Each chain expands to its canonical C10/C12 storefronts. Open one to include or exclude individual locations.
          </p>
        </div>
        <div className="ml-auto">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chains…"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-accent-500/50"
          />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                active
                  ? "bg-accent-50 text-accent-700 border-accent-200 ring-1 ring-inset ring-accent-200"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500 text-sm">Loading chains…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
          <p className="text-gray-700 text-sm font-medium">No chains here.</p>
          <p className="text-gray-500 text-xs mt-1">
            {filter === "all"
              ? "Upload a Customer Master with multi-location accounts to populate your chains."
              : "Nothing matches this filter."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Chain</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-500">Included / canonical</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Signals</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((c) => {
                const href = `#/brand/${encodeURIComponent(slug)}/chains/${encodeURIComponent(c.tenant_chain_id)}`;
                return (
                  <tr key={c.tenant_chain_id} className="hover:bg-gray-50/70">
                    <td className="px-4 py-3">
                      <a href={href} className="text-left font-medium text-gray-900 hover:text-accent-700">
                        {c.chain_name}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {(c.included_member_count || 0).toLocaleString()}
                      <span className="text-gray-400"> / {(c.canonical_location_count || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge n={c.cm_direct_count} label="CM" cls="bg-violet-50 text-violet-700 border-violet-200" />
                        <Badge n={c.wtb_confirmed_count} label="WTB" cls="bg-emerald-50 text-emerald-700 border-emerald-200" />
                        <Badge n={c.adspend_confirmed_count} label="ad-spend" cls="bg-amber-50 text-amber-700 border-amber-200" />
                        <Badge n={c.account_only_license_count} label="account-only" cls="bg-gray-100 text-gray-600 border-gray-200" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={href}
                        className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                      >
                        Open
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
